/**
 * L'écran des litiges — la liste de travail de ce qu'il reste à régler.
 *
 * Cadrage « Stafa v2 — Étape 8 : les litiges », validé le 05/09/2026. Maquette
 * `docs/maquettes/litiges.html`.
 *
 * Même découpe que partout : `VueLitiges` dessine, `EcranLitiges` décide, et le
 * calcul est dans `litiges.ts`, en fonctions pures.
 *
 *
 * TROIS CHOIX QUI SE VOIENT
 * ---------------------------------------------------------------------------
 *
 *   - **les OUVERTS par défaut.** C'est une liste de travail, pas un journal.
 *     Les résolus restent accessibles par le filtre : on cherche parfois ce qui
 *     avait été convenu il y a six mois ;
 *
 *   - **le décompte est la moitié de l'écran.** « 4 litiges ouverts — le plus
 *     ancien depuis 73 jours · 3 ordres bloqués en facturation, 6 030,00 € HT ».
 *     Sans la seconde moitié, la liste n'est qu'un pense-bête ; avec elle,
 *     c'est de l'argent qui attend. Il vient de l'API, compté sur TOUS les
 *     ouverts — un filtre ne doit pas faire croire que la somme a baissé ;
 *
 *   - **la colonne Ordre dit le statut de l'ORDRE**, pas celui du litige. Le
 *     cadrage insiste : « un OT peut être livré et en litige ». Les deux ne se
 *     confondent pas, et c'est justement leur écart qui bloque une facture.
 *
 *
 * CET ÉCRAN OUVRE LA RÉSOLUTION, IL NE LA REFAIT PAS
 * ---------------------------------------------------------------------------
 * La boîte vient de `EcranOrdres.tsx`, où elle sert déjà au bandeau de la carte
 * d'étape. Deux boîtes qui demandent la même chose divergeraient au premier
 * champ ajouté.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  lireLitiges,
  lireMotifsLitige,
  type DecompteLitiges,
  type EchecApi,
  type Litige,
} from './api.js';
import { BoiteResolution } from './EcranOrdres.js';
import { resoudreUnLitige } from './api.js';
import { ageDit, decompteLitiges, delaiMoyen, etapeDite } from './litiges.js';
import {
  classeStatut,
  enEuros,
  enFrancais,
  statutDit,
  type MotifLitige,
} from './ot.js';

export type EtatLitiges =
  | { readonly nom: 'chargement' }
  | { readonly nom: 'panne' }
  | {
      readonly nom: 'prete';
      readonly litiges: readonly Litige[];
      readonly decompte: DecompteLitiges;
    };

export type EtatFiltre = 'ouverts' | 'resolus' | 'tous';

function Absent() {
  return <span className="absent">—</span>;
}

// ---------------------------------------------------------------------------
// La vue
// ---------------------------------------------------------------------------

export interface ProprietesVueLitiges {
  readonly etat: EtatLitiges;
  readonly filtre: EtatFiltre;
  readonly motif: string;
  readonly recherche: string;
  readonly motifs: readonly MotifLitige[];
  readonly resolution: {
    readonly litigeId: string;
    readonly texte: string;
    readonly jour: string;
    readonly heure: string;
  } | null;
  readonly envoi: boolean;
  readonly refus: string | null;
  readonly quandFiltre: (filtre: EtatFiltre) => void;
  readonly quandMotif: (motif: string) => void;
  readonly quandRecherche: (texte: string) => void;
  readonly quandResoudre: (litigeId: string) => void;
  readonly quandChampResolution: (
    cle: 'texte' | 'jour' | 'heure',
    valeur: string,
  ) => void;
  readonly quandEnregistrerResolution: () => void;
  readonly quandFermerResolution: () => void;
  readonly quandOuvrirOrdre: (otId: string) => void;
}

export function VueLitiges(p: ProprietesVueLitiges) {
  const litiges = p.etat.nom === 'prete' ? p.etat.litiges : [];
  const moyen = delaiMoyen(litiges);

  return (
    <div className="corps corps-ot">
      <section className="liste">
        {p.resolution !== null && (
          <BoiteResolution
            saisie={p.resolution}
            envoi={p.envoi}
            refus={p.refus}
            quandChamp={p.quandChampResolution}
            quandFermer={p.quandFermerResolution}
            quandEnregistrer={p.quandEnregistrerResolution}
          />
        )}

        <div className="barre-outils">
          <input
            className="champ recherche"
            type="search"
            placeholder="N° d’OT, client, site, ce qui s’est passé…"
            value={p.recherche}
            onChange={(e) => {
              p.quandRecherche(e.target.value);
            }}
          />
          <label className="champ-date">
            <span className="etiquette">Motif</span>
            <select
              className="champ"
              value={p.motif}
              onChange={(e) => {
                p.quandMotif(e.target.value);
              }}
            >
              <option value="">Tous</option>
              {p.motifs.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.nom}
                </option>
              ))}
            </select>
          </label>
          <label className="champ-date">
            <span className="etiquette">État</span>
            <select
              className="champ"
              value={p.filtre}
              onChange={(e) => {
                p.quandFiltre(e.target.value as EtatFiltre);
              }}
            >
              <option value="ouverts">Ouverts</option>
              <option value="resolus">Résolus</option>
              <option value="tous">Tous</option>
            </select>
          </label>
        </div>

        {p.etat.nom === 'chargement' && <p className="attente">Chargement…</p>}
        {p.etat.nom === 'panne' && (
          <p className="bandeau-refus">La liste des litiges n’a pas abouti.</p>
        )}

        {p.etat.nom === 'prete' && (
          <>
            {/* LA PHRASE QUI FAIT L'ÉCRAN. Elle porte sur TOUS les ouverts, y
                compris ceux que le filtre cache : l'argent bloqué ne diminue
                pas parce qu'on regarde ailleurs. */}
            <p className="decompte">
              {decompteLitiges(p.etat.decompte)}
              {p.filtre !== 'ouverts' && moyen !== null && (
                <span className="sourdine">
                  {' '}
                  · délai moyen de résolution : {moyen} jour{moyen > 1 ? 's' : ''}
                </span>
              )}
            </p>

            {litiges.length === 0 ? (
              <div className="vide">
                <p>
                  {p.filtre === 'ouverts'
                    ? 'Aucun litige ouvert. Ils s’ouvrent depuis la fiche d’un ordre, sur l’étape où l’incident a eu lieu.'
                    : 'Aucun litige ne correspond à ces filtres.'}
                </p>
              </div>
            ) : (
              <div className="tableau-cadre">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">N° d’OT</th>
                      <th scope="col" className="col-souple">
                        Client
                      </th>
                      <th scope="col" className="col-large col-souple">
                        Étape
                      </th>
                      <th scope="col">Motif</th>
                      <th scope="col" className="col-large">
                        Ce qui s’est passé
                      </th>
                      <th scope="col">Ouvert le</th>
                      <th scope="col">Depuis</th>
                      <th scope="col">Ordre</th>
                      {p.filtre !== 'ouverts' && <th scope="col">Réglé</th>}
                      <th scope="col" aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {litiges.map((l) => {
                      const age = ageDit(l.jours_ouvert);
                      return (
                        <tr
                          className="rangee"
                          key={l.id}
                          tabIndex={0}
                          onClick={() => {
                            p.quandOuvrirOrdre(l.ot_id);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              p.quandOuvrirOrdre(l.ot_id);
                            }
                          }}
                        >
                          <td className="code">{l.ot_numero}</td>
                          <td className="col-souple">{l.client}</td>
                          <td className="col-large col-souple">
                            <span className="note-litige" title={etapeDite(l)}>
                              {etapeDite(l)}
                            </span>
                          </td>
                          <td>{l.motif ?? <Absent />}</td>
                          {/* LA NOTE EST COUPÉE À LA LARGEUR DE SA COLONNE,
                              et le texte entier reste en infobulle. MESURÉ le
                              07/09/2026 : sans cette borne, le tableau
                              débordait de 53 px de son cadre en 1280 px de
                              fenêtre — une note est du texte libre, elle n'a
                              aucune longueur maximale. */}
                          <td className="col-large">
                            {l.note === null ? (
                              <Absent />
                            ) : (
                              <span className="note-litige" title={l.note}>
                                {l.note}
                              </span>
                            )}
                          </td>
                          <td className="code">
                            {enFrancais(l.ouvert_le.slice(0, 10)) ?? <Absent />}
                            <span className="secondaire">{l.ouvert_par}</span>
                          </td>
                          {/* « DEPUIS » EST UN NOMBRE DE JOURS, pas une date à
                              soustraire de tête, et LE CHIFFRE porte le rouge —
                              pas un pavé derrière lui (cadrage §7). Un litige
                              résolu n'a plus d'âge : il a un délai. */}
                          <td className={`code age-${age.couleur}`}>
                            {l.resolu_le === null ? age.texte : <Absent />}
                          </td>
                          <td>
                            <span className={`statut ${classeStatut(l.ot_statut)}`}>
                              {statutDit(l.ot_statut)}
                            </span>
                          </td>
                          {p.filtre !== 'ouverts' && (
                            <td>
                              {l.resolu_le === null ? (
                                <Absent />
                              ) : (
                                <>
                                  <span className="code">
                                    {enFrancais(l.resolu_le.slice(0, 10))}
                                  </span>
                                  {l.resolution !== null && (
                                    <span className="secondaire">{l.resolution}</span>
                                  )}
                                </>
                              )}
                            </td>
                          )}
                          <td className="actions-liste">
                            {l.resolu_le === null && (
                              <button
                                className="lien-action"
                                type="button"
                                disabled={p.envoi}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  p.quandResoudre(l.id);
                                }}
                              >
                                Résoudre
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* L'EXPLICATION EST RETIRÉE (maquettes validées le 31/10/2026), LE
                CHIFFRE EST GARDÉ : c'était le seul endroit où se lisait le prix
                de vente des ordres bloqués par un litige. */}
            {p.etat.decompte.ouverts > 0 && p.filtre === 'ouverts' && (
              <p className="aide-saisie">
                Facturation bloquée : {enEuros(p.etat.decompte.montant) ?? '—'} HT.
              </p>
            )}
          </>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// L'écran
// ---------------------------------------------------------------------------

const REFUS: Readonly<Record<EchecApi, string>> = {
  acces: 'Votre accès a été refusé. Reconnectez-vous.',
  introuvable: 'Ce litige n’existe plus. Rechargez l’écran.',
  injoignable: 'Stafa ne répond pas. Vérifiez votre connexion et réessayez.',
  code_pris: 'Ce nom est déjà pris.',
  invalide: 'La résolution est obligatoire : dites comment le litige a été réglé.',
  conflit: 'Deux périodes se recouvrent.',
  encore_actif: 'Désactivez d’abord.',
  encore_rattache: 'Des fiches s’y rattachent encore.',
  ordre_annule: 'Cet ordre est annulé.',
  ordre_sans_etape: 'Cet ordre n’a aucune étape.',
  etape_validee:
    'Une étape déjà validée ne se déplace plus, et ne perd plus son camion.',
  jour_sans_etape: 'Aucune étape ce jour-là.',
  etape_en_tournee: 'Une étape de cet ordre appartient à une tournée.',
  // LES DEUX REFUS DU BOUCLAGE D'UNE JOURNÉE, ajoutés le 14/09/2026. Cette
  // carte doit rester EXHAUSTIVE : c'est elle qui garantit qu'aucun refus de
  // l'API ne s'affichera un jour sous forme de code brut.
  journee_cloturee:
    'Cette journée est clôturée. Rouvrez-la avant de la corriger.',
  journee_sans_chainage:
    'La journée précédente de ce camion ne dit pas où il s’est arrêté : renseignez son lieu de fin d’abord.',
  litige_deja_ouvert:
    'Cette étape a déjà un litige ouvert : résolvez-le avant d’en ouvrir un autre.',
  litige_deja_resolu:
    'Ce litige est déjà résolu. Ouvrez-en un nouveau sur l’étape si l’incident se répète.',
  // LES SIX REFUS DE LA FACTURATION, ajoutés le 09/09/2026. Ils n'arrivent pas
  // sur cet écran — la carte reste exhaustive, c'est ce qui garantit qu'aucun
  // refus de l'API ne s'affichera un jour sous forme de code brut.
  litige_ouvert: 'Cet ordre porte un litige ouvert : il ne se facture pas.',
  deja_facture: 'Cet ordre est déjà porté par une facture.',
  rien_a_facturer: 'Aucun de ces ordres n’attend de facture.',
  facture_emise: 'Cette pièce est émise : elle se corrige par un avoir.',
  facture_brouillon: 'Un brouillon ne s’envoie pas : validez-le d’abord.',
  facture_vide: 'Une facture sans ligne ne se valide pas.',
  // AJOUTÉ LE 09/09/2026. Un ordre parti en facturation ne se modifie plus :
  // il faut le retirer de la facturation d'abord.
  ordre_en_facturation:
    'Cet ordre est parti en facturation : retirez-le de la facturation avant de le modifier.',
  // LES CINQ REFUS DU RECOUVREMENT, ajoutés le 10/09/2026. Chacun dit la SUITE,
  // pas seulement le refus : c'est ce qui distingue un état d'une saisie fautive.
  affectation_excessive:
    'Vous affectez plus que ce qui a été reçu : corrigez la ventilation.',
  piece_non_emise:
    'Cette pièce est un brouillon : validez-la avant de la régler ou de la relancer.',
  piece_autre_client: 'Cette pièce appartient à un autre client.',
  gel_deja_actif:
    'Ce client a déjà un gel en cours : levez-le avant d’en poser un autre.',
  gel_absent: 'Ce client n’a aucun gel en cours.',
  // LES DEUX REFUS DE L'ANNULATION DE VALIDATION, ajoutés le 10/09/2026.
  numero_pas_le_dernier:
    'Cette pièce ne porte pas le dernier numéro de sa série : lui rendre son numéro laisserait un trou. Corrigez-la par un avoir.',
  facture_reglee:
    'Un règlement est affecté à cette pièce : détachez-le avant d’annuler la validation.',
  // AJOUTÉ LE 10/09/2026, sur un 500 constaté EN PRODUCTION : la base
  // refusait déjà (relance_pieces pointe sur factures en `on delete
  // restrict`), mais son refus sortait en erreur interne et l'écran
  // parlait d'une panne.
  piece_relancee:
    'Une relance a été envoyée sur cette pièce : elle ne peut plus être supprimée. Corrigez-la par un avoir.',
  facture_sans_echeance:
    'Cette pièce n’a pas de date d’échéance : sans elle, le recouvrement ne saura jamais qu’elle est en retard.',
  // AJOUTÉ LE 11/09/2026 avec le rattachement Pennylane. Distinct de
  // `code_pris` : le geste de réparation n'est pas le même. Le message NOMME
  // ce qu'il faut regarder — sans quoi il faudrait ouvrir les neuf autres
  // fiches pour trouver celle qui tient déjà ce client.
  pennylane_deja_rattache:
    'Ce client Pennylane est déjà rattaché à une autre fiche de l’annuaire. Le plus souvent, c’est que deux fiches Stafa désignent le même client : vérifiez vos doublons.',
  // AJOUTÉS LE 11/09/2026 avec l'envoi d'une facture chez Pennylane. Deux
  // messages et pas un : le premier mène à un AUTRE ÉCRAN, le second demande
  // de corriger la pièce. L'écran ne propose pas la même suite.
  pennylane_client_non_rattache:
    'Le client de cette facture n’est pas rattaché à un client Pennylane. Allez dans Paramètres → Pennylane pour le rattacher, puis réessayez.',
  pennylane_impossible:
    'Cette facture n’a pas pu être envoyée chez Pennylane. Le détail est affiché sous ce message : il vient de Pennylane et dit ce qui ne va pas.',
  facture_pas_deposee:
    'Cette facture n\u2019est pas encore d\u00e9pos\u00e9e chez Pennylane : envoyez-la d\u2019abord.',
  facture_modifiee_depuis_apercu:
    'Cette facture a changé depuis le dernier aperçu. Recliquez « Aperçu Pennylane », vérifiez le document, puis validez.',
  // LES QUATRE REFUS DE L'AVOIR, ajoutés le 11/09/2026. Quatre et pas un :
  // chacun porte une SUITE différente — ouvrir l'avoir existant, corriger un
  // montant, ou rien du tout quand le geste n'a pas de sens.
  avoir_sur_avoir:
    'On n’établit pas un avoir sur un avoir. Si cet avoir est faux, c’est une facture qu’il faut établir.',
  facture_deja_avoiree:
    'Cette facture est déjà intégralement avoirée : un second avoir rendrait au client plus qu’il n’a payé. Ouvrez l’avoir existant.',
  avoir_trop_grand:
    'Cet avoir dépasse ce qui reste à avoirer sur cette facture. Le détail affiché sous ce message donne le montant maximal.',
  avoir_impossible:
    'Cet avoir ne peut pas être établi. Le détail affiché sous ce message dit ce qui s’y oppose.',
  // AJOUTÉ LE 10/09/2026. Distinct de `injoignable`, qui parlait de connexion
  // alors que le service avait bel et bien répondu.
  service:
    'Le service a répondu une erreur inattendue. Ce n’est pas votre connexion. Ouvrez la console (F12) : le code y est écrit.',
};

export interface ProprietesLitiges {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
  readonly quandOuvrirOrdre: (otId: string) => void;
}

export function EcranLitiges({
  jeton,
  quandAccesPerdu,
  quandOuvrirOrdre,
}: ProprietesLitiges) {
  const [etat, setEtat] = useState<EtatLitiges>({ nom: 'chargement' });
  const [filtre, setFiltre] = useState<EtatFiltre>('ouverts');
  const [motif, setMotif] = useState('');
  const [recherche, setRecherche] = useState('');
  const [motifs, setMotifs] = useState<readonly MotifLitige[]>([]);
  const [resolution, setResolution] = useState<{
    readonly litigeId: string;
    readonly texte: string;
    readonly jour: string;
    readonly heure: string;
  } | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [refus, setRefus] = useState<string | null>(null);

  const avecJeton = useCallback(async (): Promise<string | null> => {
    const j = await jeton();
    if (j === null) quandAccesPerdu();
    return j;
  }, [jeton, quandAccesPerdu]);

  /** Le référentiel des motifs, chargé UNE FOIS : il ne bouge pas d'un filtre à l'autre. */
  useEffect(() => {
    let abandonne = false;
    void (async () => {
      const j = await avecJeton();
      if (j === null || abandonne) return;
      const lus = await lireMotifsLitige(j);
      if (!abandonne && lus.etat === 'ok') setMotifs(lus.valeur);
    })();
    return () => {
      abandonne = true;
    };
  }, [avecJeton]);

  const relire = useCallback(async () => {
    const j = await avecJeton();
    if (j === null) return;
    const lus = await lireLitiges(j, { etat: filtre, motif, q: recherche });
    if (lus.etat === 'ok') {
      setEtat({ nom: 'prete', ...lus.valeur });
    } else {
      if (lus.cause === 'acces') quandAccesPerdu();
      setEtat({ nom: 'panne' });
    }
  }, [avecJeton, filtre, motif, recherche, quandAccesPerdu]);

  useEffect(() => {
    let abandonne = false;
    setEtat({ nom: 'chargement' });
    // LA RECHERCHE ATTEND QU'ON AIT FINI DE TAPER — 250 ms. Sans ce délai,
    // chaque lettre partirait à l'API, et les réponses reviendraient dans le
    // désordre.
    const minuteur = setTimeout(() => {
      if (!abandonne) void relire();
    }, 250);
    return () => {
      abandonne = true;
      clearTimeout(minuteur);
    };
  }, [relire]);

  const enregistrer = () => {
    if (resolution === null) return;
    if (resolution.texte.trim() === '') {
      setRefus('Dites comment le litige a été réglé.');
      return;
    }
    setEnvoi(true);
    setRefus(null);
    void (async () => {
      const j = await avecJeton();
      if (j === null) {
        setEnvoi(false);
        return;
      }
      const resoluLe =
        resolution.jour === '' || resolution.heure === ''
          ? null
          : new Date(`${resolution.jour}T${resolution.heure}:00`).toISOString();
      const fait = await resoudreUnLitige(j, resolution.litigeId, {
        resolution: resolution.texte.trim(),
        ...(resoluLe === null ? {} : { resolu_le: resoluLe }),
      });
      setEnvoi(false);
      if (fait.etat !== 'ok') {
        if (fait.cause === 'acces') quandAccesPerdu();
        setRefus(REFUS[fait.cause]);
        return;
      }
      setResolution(null);
      await relire();
    })();
  };

  return (
    <VueLitiges
      etat={etat}
      filtre={filtre}
      motif={motif}
      recherche={recherche}
      motifs={motifs}
      resolution={resolution}
      envoi={envoi}
      refus={refus}
      quandFiltre={setFiltre}
      quandMotif={setMotif}
      quandRecherche={setRecherche}
      quandResoudre={(litigeId) => {
        const maintenant = new Date();
        const deuxChiffres = (n: number) => String(n).padStart(2, '0');
        setRefus(null);
        setResolution({
          litigeId,
          texte: '',
          jour: `${String(maintenant.getFullYear())}-${deuxChiffres(
            maintenant.getMonth() + 1,
          )}-${deuxChiffres(maintenant.getDate())}`,
          heure: `${deuxChiffres(maintenant.getHours())}:${deuxChiffres(
            maintenant.getMinutes(),
          )}`,
        });
      }}
      quandChampResolution={(cle, valeur) => {
        setResolution((actuelle) =>
          actuelle === null ? actuelle : { ...actuelle, [cle]: valeur },
        );
      }}
      quandEnregistrerResolution={enregistrer}
      quandFermerResolution={() => {
        setResolution(null);
        setRefus(null);
      }}
      quandOuvrirOrdre={quandOuvrirOrdre}
    />
  );
}
